import { isFuture } from 'date-fns';
import omit from 'lodash/omit';
import { unstable_cache } from 'next/cache';
import slugify from 'react-slugify';

import { FILTER_BY_DEALERSHIP } from '@/constants/dealership/dealership.constants';
import {
  DETAILED_VEHICLE_REQUEST_FIELDS,
  VEHICLE_REQUEST_FIELDS,
} from '@/constants/details-car/details-car.constants';
import {
  AVAILABLE_BRANDS_FIELDS,
  BRAND_AND_RANGES_NAMES_REQUEST_FIELDS,
  BRAND_AND_RANGES_REQUEST_FIELDS,
  BRAND_LIST_REQUEST_FIELDS,
  BRAND_NAMES_REQUEST_FIELDS,
  FOOTER_BRANDS_FIELDS,
  FOOTER_CARS_FIELDS,
} from '@/constants/filters/filters.constants';
import { priceOptions } from '@/constants/navigation/navigation.constants';
import { NEW_CARS_FILTER } from '@/constants/new-cars/new-cars.constants';
import {
  FILTER_ACTIVE_VEHICLES,
  FILTER_BY_BODY_TYPE_ID,
  FILTER_BY_BRAND,
  FILTER_BY_DUOPORTA_ID,
  FILTER_BY_MMCODE,
  FILTER_BY_PRICE_RANGE,
  FILTER_BY_PROVINCE,
  FILTER_BY_STATUS,
  FILTER_BY_TYPE,
  FILTER_BY_YEAR,
  PAGE_SIZE,
  PROVINCE_REQUEST_FIELDS,
  SIMILAR_VEHICLE_REQUEST_FIELDS,
  sortOptions,
  VEHICLES_REQUEST_FIELDS,
} from '@/constants/used-cars/used-cars.constants';
import { PATH_COUNT } from '@/constants/vars.constants';
import { apiService, directusService, limiter } from '@/services/api.service';
import { mapVehicleResponse } from '@/services/details-car/details-car.service';
import {
  getOnlyKia,
  mapBodyTypesPaths,
  mapBodyTypesResponse,
  mapBrandAndRangesResponse,
  mapBrandRangeCountResponse,
  mapBrands,
  mapBrandsModelsPaths,
  mapBrandsPaths,
  mapCarTypesResponse,
  mapDealershipsResponse,
  mapLocationsResponse,
  mapProvincesCitiesPaths,
  mapProvincesDealershipsPaths,
  mapProvincesPaths,
  mapProvincesResponse,
} from '@/services/filters/filters.service';
import {
  ceilMileageToFiftyThousand,
  ceilPriceToHundredThousand,
  floorMileageToFiftyThousand,
} from '@/services/string-format.service';
import { mapVehiclesResponse } from '@/services/used-cars/used-cars.service';
import { VehicleReqExtended } from '@/types/details-car/details-car.types';
import { FiltersTypes, SpecCountType } from '@/types/filters/filters.types';
import { PopularBodyType, PopularMake } from '@/types/home/home.types';
import {
  BodyTypesReq,
  BrandNameReq,
  BrandReq,
  ConsumptionReq,
  FooterBrandReq,
  MileageReq,
  PriceReq,
  ProvinceReq,
  RangeNameReq,
  SitemapVehicleUrl,
  VehicleReq,
  YearReq,
} from '@/types/request/request.types';
import {
  SortOption,
  UserCarsFilterQuery,
  Vehicle,
  VehicleType,
} from '@/types/used-cars/used-cars.types';

import { DateService } from './date.service';

export const getBrandModelCounts = limiter.wrap(
  async (
    type?: 'used' | 'demo',
    dealershipId?: string,
    saleEndDate?: Date,
    additionalFields = [] as string[],
  ) => {
    const vehicleFilter = {
      status: {
        _eq: 'active',
      },
      ...(type ? { type: { _eq: type === 'used' ? '1' : '3' } } : {}),
      ...(dealershipId ? { dealership_id: { _eq: dealershipId } } : {}),
    };

    /* This is required because directus now requires a full path on _add / _or filtering.
*
* As an example if doing an _and filter on the brands collection but on the vehicles field it needs to look like this:
*
* filter: {
*   _and: {
      { vehicles: { sale_end_date: { _gt: 'now()' } } },
      { vehicles: { sale_end_date: { _nnull: true } } }
*   }
* }
*
* instead of:
*
* filter: {
*   _and: {
      { sale_end_date: { _gt: 'now()' } },
      { sale_end_date: { _nnull: true } }
*   }
* }
*
* but this is not the case for deep filtering directly on the vehicles field hence the need for 2 filter objects
*
  */
    const nestedFilter = {
      _and: [
        ...Object.keys(vehicleFilter).map((key) => ({
          vehicles: { [key]: (vehicleFilter as any)[key] },
        })),
      ],
    };

    const flatFilter = {
      _and: [
        ...Object.keys(vehicleFilter).map((key) => ({
          [key]: (vehicleFilter as any)[key],
        })),
      ],
    };

    if (saleEndDate) {
      nestedFilter._and.push(
        { vehicles: { sale_end_date: { _gt: 'now()' } } },
        { vehicles: { sale_end_date: { _nnull: true } } },
      );
      flatFilter._and.push(
        { sale_end_date: { _gt: 'now()' } },
        { sale_end_date: { _nnull: true } },
      );
    }

    const { data } = await directusService.get('items/brands', {
      params: {
        fields: [
          'id',
          'name',
          'code',
          'logo',
          'vehicles.id',
          'range_ids.id',
          'range_ids.name',
          'range_ids.code',
          'range_ids.vehicles.id',
          'alias',
          ...additionalFields,
        ].join(','),
        sort: 'name',
        filter: {
          'count(vehicles)': {
            _gt: 0,
          },
          ...nestedFilter,
        },
        deep: {
          range_ids: {
            sort: 'name',
            _filter: {
              'count(vehicles)': {
                _gt: 0,
              },
              ...nestedFilter,
            },
            _limit: -1,
            vehicles: {
              _filter: flatFilter,
              _limit: -1,
            },
          },
          vehicles: {
            _limit: -1,
            _filter: flatFilter,
          },
        },
      },
    });

    return mapBrandRangeCountResponse(data);
  },
);

// Add cached version of getBrandModelCounts
export const getCachedBrandModelCounts = unstable_cache(
  async (
    type?: 'used' | 'demo',
    dealershipId?: string,
    saleEndDate?: Date,
    additionalFields = [] as string[],
  ) => getBrandModelCounts(type, dealershipId, saleEndDate, additionalFields),
  ['brand-model-counts'],
  {
    tags: ['brand-model-counts'],
    revalidate: 60 * 120, // 2 hours
  },
);

export const getBrands = limiter.wrap(
  async (dealershipId?: string, saleEndDate?: Date) => {
    const { data } = await directusService.get<BrandReq[]>('items/brands', {
      params: {
        fields: BRAND_AND_RANGES_REQUEST_FIELDS,
        deep: {
          range_ids: {
            _limit: -1,
            vehicles: {
              _limit: -1,
            },
          },
          vehicles: {
            _limit: -1,
          },
        },
      },
    });

    const brands = data;

    if (saleEndDate) {
      for (const brand of data) {
        brand.vehicles = brand.vehicles.filter((vehicle: any) => {
          if (vehicle.sale_end_date) {
            return isFuture(vehicle.sale_end_date);
          }
          return false;
        });

        for (const range of brand.range_ids) {
          range.vehicles = range.vehicles.filter((vehicle: any) => {
            if (vehicle.sale_end_date) {
              return isFuture(vehicle.sale_end_date);
            }
            return false;
          });
        }
      }
    }

    const mapped = mapBrandAndRangesResponse(brands, dealershipId);

    return mapped;
  },
);

export const getBrandNames = limiter.wrap(async () => {
  const { data } = await directusService.get<BrandNameReq[]>('items/brands', {
    params: {
      fields: ['name', 'id'],
    },
  });

  return data as BrandNameReq[];
});

export const getRangeNamesForBrand = limiter.wrap(async (brandId) => {
  const { data } = await directusService.get<RangeNameReq[]>('items/ranges', {
    params: {
      fields: ['name', 'id'],
      filter: {
        brand_id: {
          _eq: brandId,
        },
      },
    },
  });

  return data;
});

export const getBrandById = limiter.wrap(async (id: number | undefined) => {
  if (!id) return null;
  const { data } = await directusService.get(`items/brands/${id}`);

  return data;
});

export const getRangeById = limiter.wrap(async (id: number | undefined) => {
  if (!id) return null;
  const { data } = await directusService.get(`/items/ranges/${id}`);

  return data;
});

export const getBrandList = limiter.wrap(async () => {
  const { data } = await directusService.get<BrandReq[]>('items/brands', {
    params: { fields: BRAND_LIST_REQUEST_FIELDS },
  });

  return data;
});

export const getAllBrands = async () => {
  const { data } = await directusService.get<BrandReq[]>('items/brands', {
    params: { fields: BRAND_AND_RANGES_REQUEST_FIELDS },
  });

  return (data as BrandReq[])?.map(mapBrands);
};

const getSimilarVehiclesToCarRequest = limiter.wrap(
  async (filter): Promise<VehicleReqExtended[]> => {
    const { data } = await directusService.get<VehicleReqExtended[]>(
      '/items/vehicles',
      {
        params: {
          fields: SIMILAR_VEHICLE_REQUEST_FIELDS.join(','),
          limit: PAGE_SIZE,
          offset: 0,
          sort: sortOptions?.at(0)?.value,
          filter,
        },
      },
    );

    return data;
  },
);

interface SimilarVehiclesConfig {
  duoportaId?: string;
  mmCode?: string;
  year?: string;
  dealershipId?: string;
  vehicleId?: string;
  vehiclePrice?: number;
  isMicrosite?: boolean;
}

export const getSimilarVechiclesToCar = limiter.wrap(
  async (config: SimilarVehiclesConfig): Promise<Vehicle[]> => {
    const {
      duoportaId,
      mmCode,
      year,
      dealershipId,
      vehicleId,
      vehiclePrice,
      isMicrosite = false,
    } = config;

    let vehicles: VehicleReqExtended[] = [];
    const excludeFilter = vehicleId ? { id: { _neq: vehicleId } } : {};

    // Microsite flow - only look for vehicles within the same dealership
    if (isMicrosite && dealershipId) {
      // First try to find similar vehicles with the same specifications in the dealership
      if (duoportaId) {
        vehicles = await getSimilarVehiclesToCarRequest({
          ...FILTER_ACTIVE_VEHICLES,
          ...FILTER_BY_DUOPORTA_ID(duoportaId),
          ...FILTER_BY_DEALERSHIP(dealershipId),
          ...excludeFilter,
        });
      }

      // If no duoporta ID or not enough vehicles, try with mmCode and year
      if (!duoportaId || (vehicles && vehicles.length < 3)) {
        const mmCodeFilter = mmCode ? FILTER_BY_MMCODE(mmCode) : {};
        const yearFilter = year ? FILTER_BY_YEAR(year) : {};

        vehicles = await getSimilarVehiclesToCarRequest({
          ...FILTER_ACTIVE_VEHICLES,
          ...mmCodeFilter,
          ...yearFilter,
          ...FILTER_BY_DEALERSHIP(dealershipId),
          ...excludeFilter,
        });
      }

      // If still not enough vehicles or no vehicles found, look for vehicles in the same price range
      if ((!vehicles || vehicles.length < 3) && vehiclePrice) {
        vehicles = await getSimilarVehiclesToCarRequest({
          ...FILTER_ACTIVE_VEHICLES,
          ...FILTER_BY_DEALERSHIP(dealershipId),
          ...FILTER_BY_PRICE_RANGE(vehiclePrice),
          ...excludeFilter,
        });
      }
    }
    // Non-microsite flow - first apply existing rules, then fall back to price
    else {
      // Try with duoportaId first if available
      if (duoportaId) {
        vehicles = await getSimilarVehiclesToCarRequest({
          ...FILTER_ACTIVE_VEHICLES,
          ...FILTER_BY_DUOPORTA_ID(duoportaId),
          ...excludeFilter,
        });
      }

      // If not enough vehicles, try with mmCode and year
      if (!duoportaId || !vehicles || vehicles.length < 3) {
        const mmCodeFilter = mmCode ? FILTER_BY_MMCODE(mmCode) : {};
        const yearFilter = year ? FILTER_BY_YEAR(year) : {};

        // Only proceed if we have at least one of mmCode or year
        if (mmCode || year) {
          vehicles = await getSimilarVehiclesToCarRequest({
            ...FILTER_ACTIVE_VEHICLES,
            ...mmCodeFilter,
            ...yearFilter,
            ...excludeFilter,
          });
        }
      }

      // Finally, if we still don't have enough vehicles, use price range
      if ((!vehicles || vehicles.length < 3) && vehiclePrice) {
        vehicles = await getSimilarVehiclesToCarRequest({
          ...FILTER_ACTIVE_VEHICLES,
          ...FILTER_BY_PRICE_RANGE(vehiclePrice),
          ...excludeFilter,
        });
      }
    }

    return mapVehiclesResponse(vehicles);
  },
);

export const getBrandsPaths = limiter.wrap(async () => {
  const { data } = await directusService.get<BrandReq[]>('items/brands', {
    params: { fields: BRAND_NAMES_REQUEST_FIELDS },
  });

  return mapBrandsPaths(data.slice(0, PATH_COUNT));
});

export const getBrandsModelsPaths = limiter.wrap(async () => {
  const { data } = await directusService.get<BrandReq[]>('items/brands', {
    params: { fields: BRAND_AND_RANGES_NAMES_REQUEST_FIELDS },
  });

  return mapBrandsModelsPaths(data.slice(0, PATH_COUNT));
});

export const getProvincesPaths = limiter.wrap(async () => {
  const { data } = await directusService.get<ProvinceReq[]>('items/provinces', {
    params: {
      fields: 'name',
    },
  });

  return mapProvincesPaths(data.slice(0, PATH_COUNT));
});

export const getProvincesCitiesPaths = limiter.wrap(async () => {
  const { data } = await directusService.get<ProvinceReq[]>('items/provinces', {
    params: {
      fields: 'name, cities.name',
    },
  });

  return mapProvincesCitiesPaths(data.slice(0, PATH_COUNT));
});

export const getProvincesDealershipsPaths = limiter.wrap(async () => {
  const { data: provinces } = await directusService.get<ProvinceReq[]>(
    'items/provinces',
    {
      params: {
        fields: 'name, id',
      },
    },
  );

  const { data: dilerships } = await directusService.get<ProvinceReq[]>(
    'items/dealerships',
    {
      params: {
        fields: 'id, name, province',
        filter: {
          ...FILTER_BY_STATUS('published'),
        },
      },
    },
  );

  return mapProvincesDealershipsPaths(
    provinces.slice(0, PATH_COUNT),
    dilerships.slice(0, PATH_COUNT),
  );
});

export const getBodyTypesPaths = limiter.wrap(async () => {
  const { data } = await directusService.get<BodyTypesReq[]>(
    'items/spec_body_types',
    {
      params: { fields: 'id, name' },
    },
  );

  return mapBodyTypesPaths(data.slice(0, PATH_COUNT));
});

export const getPrice = limiter.wrap(async (dealershipId?: string) => {
  const { data } = await directusService.get<PriceReq[]>('items/vehicles', {
    params: {
      fields: 'sales_price_including_vat',
      sort: '-sales_price_including_vat',
      limit: '1',
      filter: {
        ...FILTER_ACTIVE_VEHICLES,
        ...FILTER_BY_DEALERSHIP(dealershipId),
        ...{ sales_price_including_vat: { _neq: null } }, // Filter out null prices
      },
    },
  });

  return {
    min: 0,
    max: ceilPriceToHundredThousand(
      data?.at(0)?.sales_price_including_vat,
    ) as number,
  };
});

export const getBodyTypes = limiter.wrap(
  async (vehicleType?: 'used' | 'demo') => {
    let type;

    if (vehicleType) {
      type = vehicleType === 'used' ? '1' : '3';
    }

    const { data } = await directusService.get<BodyTypesReq[]>(
      'items/spec_body_types',
      {
        params: {
          fields: 'id, name, dealer_name_body_type, image, value, vehicles.id',
        },
      },
    );

    const { data: visibleBodyTypes } = await directusService.get<
      BodyTypesReq[]
    >('items/spec_body_types', {
      params: {
        fields: 'id',
        filter: {
          vehicles: {
            status: { _eq: 'active' },
            ...(type ? { type: { _eq: type } } : {}),
          },
        },
      },
    });

    return mapBodyTypesResponse(data, FiltersTypes.body, visibleBodyTypes);
  },
);

export const getColourTypes = limiter.wrap(async () => {
  const { data } = await directusService.get<BodyTypesReq[]>(
    'items/spec_body_colors',
    {
      params: { fields: 'id, name' },
    },
  );

  return mapCarTypesResponse(data, FiltersTypes.colour);
});

export const getDoorsTypes = limiter.wrap(async () => {
  const { data } = await directusService.get<BodyTypesReq[]>(
    '/items/spec_doors',
    {
      params: { fields: 'id, name' },
    },
  );

  return mapCarTypesResponse(data, FiltersTypes.doors);
});

export const getYears = limiter.wrap(async (dealershipId?: string) => {
  const { data: minYear } = await directusService.get<YearReq[]>(
    'items/vehicles',
    {
      params: {
        fields: 'year',
        sort: 'year',
        limit: '1',
        filter: {
          ...FILTER_ACTIVE_VEHICLES,
          ...FILTER_BY_DEALERSHIP(dealershipId),
        },
      },
    },
  );

  return {
    min: minYear?.at(0)?.year || '2000',
    max: DateService.getFullYear(),
  };
});

export const getMileages = limiter.wrap(async (dealershipId?: string) => {
  const { data: minMileage } = await directusService.get<MileageReq[]>(
    'items/vehicles',
    {
      params: {
        fields: 'mileage',
        sort: 'mileage',
        limit: '1',
        filter: {
          ...FILTER_ACTIVE_VEHICLES,
          ...FILTER_BY_DEALERSHIP(dealershipId),
        },
      },
    },
  );

  const { data: maxMileage } = await directusService.get<MileageReq[]>(
    'items/vehicles',
    {
      params: {
        fields: 'mileage',
        sort: '-mileage',
        limit: '1',
        filter: {
          ...FILTER_ACTIVE_VEHICLES,
          ...FILTER_BY_DEALERSHIP(dealershipId),
        },
      },
    },
  );

  return {
    min: floorMileageToFiftyThousand(minMileage?.at(0)?.mileage),
    max: ceilMileageToFiftyThousand(maxMileage?.at(0)?.mileage) as number,
  };
});

export const getConsumption = limiter.wrap(async () => {
  const { data: minConsumption } = await directusService.get<ConsumptionReq[]>(
    'items/spec_litres_100km',
    {
      params: { fields: 'id, float_value', sort: 'float_value', limit: '1' },
    },
  );

  const { data: maxConsumption } = await directusService.get<ConsumptionReq[]>(
    'items/spec_litres_100km',
    {
      params: { fields: 'id, float_value', sort: '-float_value', limit: '1' },
    },
  );

  return {
    min: minConsumption?.at(0)?.float_value,
    max: maxConsumption?.at(0)?.float_value,
  };
});

export const getVehicles = limiter.wrap(
  async (
    dealershipId?: string | null,
    filtersQuery?: UserCarsFilterQuery,
    type?: string | null,
    bodyTypeId?: number | null,
    page?: string | null,
    sort?: SortOption,
  ) => {
    const excludeUTMCodes = [
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_content',
      'utm_term',
      'utm_cta',
      'gclid',
      'fbclid',
    ];
    const filteredFiltersQuery = omit(filtersQuery, excludeUTMCodes);
    const getFilterQuery = (query: Partial<UserCarsFilterQuery>) => {
      if (!query?.sales_price_including_vat?._between) return query;
      const { sales_price_including_vat, ...newFiltersQuery } = query;

      newFiltersQuery.display_price = sales_price_including_vat;
      return newFiltersQuery;
    };

    // TODO: fix types
    const { data, meta: vehiclesMetaData }: any = await directusService.get<
      VehicleReq[]
    >('/items/vehicles', {
      params: {
        fields: VEHICLES_REQUEST_FIELDS.join(','),
        limit: PAGE_SIZE,
        offset: 0,
        meta: 'filter_count',
        page: page,
        filter: {
          ...getFilterQuery(filteredFiltersQuery),
          ...FILTER_ACTIVE_VEHICLES,
          ...FILTER_BY_TYPE(type),
          ...FILTER_BY_DEALERSHIP(dealershipId),
          ...FILTER_BY_BODY_TYPE_ID(bodyTypeId),
        },
        sort: sort ? `sort,${sort.value}` : undefined,
      },
    });

    const vehiclesData: VehicleReqExtended[] = data;

    const vehicles = vehiclesData.map((vehicle) =>
      mapVehicleResponse(vehicle, vehicle.images, null),
    );

    const vehiclesMeta = {
      filterCount: vehiclesMetaData.filter_count,
      currentPage: page ? parseInt(page) : 1,
      pageSize: PAGE_SIZE,
      sort: sort ?? null,
      isIndexSearch: false,
    };

    vehiclesMeta.filterCount = vehiclesMetaData.filter_count;

    return { vehicles, vehiclesMeta };
  },
);

export const getVehiclesByIds = limiter.wrap(async (ids: number[]) => {
  const { data } = await directusService.get<VehicleReq[]>('/items/vehicles', {
    params: {
      fields: VEHICLES_REQUEST_FIELDS.join(','),
      limit: -1,
      filter: {
        id: { _in: ids },
        ...FILTER_ACTIVE_VEHICLES,
      },
    },
  });

  const vehiclesData: VehicleReqExtended[] = data;
  const vehicles = vehiclesData.map((vehicle) =>
    mapVehicleResponse(vehicle, vehicle.images, null),
  );

  return vehicles;
});

export const getPopularVehicles = limiter.wrap(
  async (
    dealershipId?: string,
    filtersQuery?: UserCarsFilterQuery,
    type?: string,
    bodyTypeId?: number,
    limit = 15,
  ) => {
    const { data, meta: vehiclesMetaData }: any = await directusService.get<
      VehicleReq[]
    >('/items/vehicles', {
      params: {
        fields: VEHICLES_REQUEST_FIELDS.join(','),
        limit: limit,
        offset: 0,
        meta: 'filter_count',
        filter: {
          ...filtersQuery,
          ...FILTER_ACTIVE_VEHICLES,
          ...FILTER_BY_TYPE(type),
          ...FILTER_BY_DEALERSHIP(dealershipId),
          ...FILTER_BY_BODY_TYPE_ID(bodyTypeId),
        },
        sort: '-popularity',
      },
    });

    const vehiclesMeta = {
      filterCount: 0,
      currentPage: 1,
      pageSize: PAGE_SIZE,
      sort: '-popularity',
      isIndexSearch: false,
    };

    vehiclesMeta.filterCount = vehiclesMetaData.filter_count;
    const vehicles = data.map((vehicle: any) =>
      mapVehicleResponse(vehicle, vehicle.images, null),
    );

    return { vehicles, vehiclesMeta };
  },
);

interface Model {
  name: string;
  make: string;
}

const models: Model[] = [];

export const getAllVehicleModels = async (): Promise<Model[]> => {
  const { data }: any = await directusService.get<VehicleReq[]>(
    '/items/vehicles',
    {
      params: {
        fields: 'range_id.name,brand_id.name',
        limit: -1,
        filter: {
          ...FILTER_ACTIVE_VEHICLES,
          ...FILTER_BY_TYPE('all'), // used cars
        },
        sort: sortOptions[1]?.value,
      },
    },
  );

  const validData = data.filter(
    (vehicle: any) => vehicle.brand_id?.name && vehicle.range_id?.name,
  );

  validData.forEach((vehicle: any) => {
    const rangeName = vehicle.range_id?.name;
    const brandName = vehicle.brand_id?.name;

    const modelExists = models.some((model) => model.name === rangeName);

    if (rangeName && brandName && !modelExists) {
      models.push({
        name: rangeName,
        make: brandName,
      });
    }
  });

  return models;
};

export const getAllVehicleUrls = async (): Promise<SitemapVehicleUrl[]> => {
  const { data }: any = await directusService.get<VehicleReq[]>(
    '/items/vehicles',
    {
      params: {
        fields: 'range_id.name,brand_id.name,id,date_updated',
        limit: -1,
        filter: {
          ...FILTER_ACTIVE_VEHICLES,
          ...FILTER_BY_TYPE('all'),
        },
        sort: sortOptions[1]?.value,
      },
    },
  );

  const validData = data.filter(
    (vehicle: any) => vehicle.brand_id?.name && vehicle.range_id?.name,
  );

  return validData.map((vehicle: VehicleReq) => ({
    url: `/used-cars/${slugify(vehicle.brand_id.name)}/${slugify(
      vehicle.range_id.name,
    )}/${vehicle.id}`,
    date_updated: vehicle.date_updated,
  }));
};

export const getAllVehicles = limiter.wrap(
  async (
    dealershipId?: string,
    filtersQuery?: UserCarsFilterQuery,
    type?: string,
    bodyTypeId?: number,
  ) => {
    const { data, meta: vehiclesMetaData }: any = await directusService.get<
      VehicleReq[]
    >('/items/vehicles', {
      params: {
        fields: VEHICLES_REQUEST_FIELDS.join(','),
        limit: -1,
        offset: 0,
        meta: 'filter_count',
        filter: {
          ...filtersQuery,
          ...FILTER_ACTIVE_VEHICLES,
          ...FILTER_BY_TYPE(type),
          ...FILTER_BY_DEALERSHIP(dealershipId),
          ...FILTER_BY_BODY_TYPE_ID(bodyTypeId),
        },
        sort: sortOptions[1]?.value,
      },
    });

    const vehiclesMeta = {
      filterCount: 0,
      currentPage: 1,
      pageSize: PAGE_SIZE,
      sort: sortOptions[1],
    };

    vehiclesMeta.filterCount = vehiclesMetaData.filter_count;
    const vehicles = data.map((vehicle: any) =>
      mapVehicleResponse(vehicle, vehicle.images, null),
    );

    return { vehicles, vehiclesMeta };
  },
);

export const getLocations = limiter.wrap(async () => {
  const { data } = await directusService.get<ProvinceReq[]>('items/provinces', {
    params: {
      fields:
        'name, id, code, image, cities.id, cities.name, cities.code, latitude, longitute',
    },
  });

  return mapLocationsResponse(data);
});

export const getProvincesWithDealerships = async (dealershipId?: string) => {
  const { data } = await directusService.get<ProvinceReq[]>('items/provinces', {
    params: {
      fields: PROVINCE_REQUEST_FIELDS.join(','),
      filter: {
        ...FILTER_BY_STATUS('published'),
        _some: {
          dealerships: {
            _nnull: true,
          },
        },
      },
      deep: {
        dealerships: {
          _filter: {
            ...FILTER_BY_STATUS('published'),
          },
        },
      },
    },
  });

  return mapProvincesResponse(data, dealershipId);
};

export const getDealerships = limiter.wrap(
  async (provinceId?: number | string) => {
    const { data } = await directusService.get<ProvinceReq[]>(
      'items/dealerships',
      {
        params: {
          fields: 'name, id, code, city, province, status, vehicles',
          filter: {
            ...FILTER_BY_STATUS('published'),
            ...FILTER_BY_PROVINCE(provinceId),
          },
        },
      },
    );

    return mapDealershipsResponse(data);
  },
);

export const getImages = limiter.wrap(
  async (
    ids: number[] | string[] | { id: string }[],
    limit: number | null = null,
  ) => {
    const imageIds = ids.map((id) =>
      typeof id === 'string' ? id : typeof id === 'number' ? id : id.id,
    );
    const { data } = await directusService.get('/files', {
      params: {
        fields: 'id, title',
        'filter[id][_in]': imageIds,
        sort: 'sort_order',
        limit,
      },
    });

    return data;
  },
);

export const getActiveVehicle = async (
  vehicleId: string | undefined,
) => {
  if (!vehicleId) {
    return null;
  }

  const { data } = await directusService.get(`/items/vehicles`, {
    params: {
      fields: DETAILED_VEHICLE_REQUEST_FIELDS.join(','),
      filter: {
        _and: [
          { id: { _eq: vehicleId } },
          { status: { _eq: 'active' } },
        ],
      },
      limit: 1,
    },
  });

  const vehicle = data?.[0];

  if (!vehicle) {
    return null;
  }

  const mappedVehicle = mapVehicleResponse(vehicle);

  return mappedVehicle;
};

export const getVehicle = limiter.wrap(
  async (vehicleId: string | undefined) => {
    if (!vehicleId) {
      return null;
    }

    const { data } = await directusService.get(`/items/vehicles/${vehicleId}`, {
      params: {
        fields: VEHICLE_REQUEST_FIELDS.join(','),
      },
    });

    return mapVehicleResponse(data);
  },
);

export const getSimilarVechicles = limiter.wrap(
  async (brandId?: number, limit = PAGE_SIZE) => {
    const { data } = await directusService.get('/items/vehicles', {
      params: {
        fields: VEHICLES_REQUEST_FIELDS.join(','),
        limit: limit,
        offset: 0,
        sort: sortOptions?.at(0)?.value,
        filter: {
          ...FILTER_ACTIVE_VEHICLES,
          ...FILTER_BY_BRAND(brandId),
        },
      },
    });

    const vehiclesData: VehicleReqExtended[] = data;

    return vehiclesData.map((vehicle) =>
      mapVehicleResponse(vehicle, vehicle.images, null),
    );
  },
);

export const getAllAvailableBrands = limiter.wrap(async (filter = {}) => {
  const { data } = await directusService.get(
    'items/brands?deep[vehicles][_limit]=-1',
    {
      params: {
        fields: AVAILABLE_BRANDS_FIELDS,
        filter,
      },
    },
  );

  return data;
});

export const getHeaderBodyTypes = limiter.wrap(
  async (vehicleType: 'used' | 'demo') => {
    const type = vehicleType === 'used' ? '1' : '3';
    const { data } = await directusService.get('items/spec_body_types', {
      params: {
        fields: ['id', 'name'],
        filter: {
          vehicles: {
            type: {
              _eq: type,
            },
            status: {
              _eq: 'active',
            },
          },
        },
      },
    });

    return data;
  },
);

export const getHeaderBrands = async (vehicleType: 'used' | 'demo') => {
  const type = vehicleType === 'used' ? VehicleType.Used : VehicleType.Demo;
  const { data } = await directusService.get('items/brands', {
    params: {
      fields: AVAILABLE_BRANDS_FIELDS,
      deep: {
        vehicles: {
          _filter: {
            type: {
              _eq: type,
            },
            status: {
              _eq: 'active',
            },
          },
        },
      },
      filter: {
        vehicles: {
          type: {
            _eq: type,
          },
          status: {
            _eq: 'active',
          },
        },
      },
    },
  });

  return data;
};

const toTitleCase = (str: string) => {
  if (
    str !== 'BMW' &&
    str !== 'BAW' &&
    str !== 'CAM' &&
    str !== 'DFM' &&
    str !== 'DFSK' &&
    str !== 'GAZ' &&
    str !== 'GWM' &&
    str !== 'JAC' &&
    str !== 'JMC' &&
    str !== 'MG' &&
    str !== 'FAW' &&
    str !== 'TATA' &&
    str !== 'TVR' &&
    str !== 'UD Trucks' &&
    str !== 'UD TRUCKS'
  ) {
    return str
      ? str.replace(/\w\S*/g, function (txt) {
          return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
        })
      : str;
  } else {
    return str;
  }
};

const capitalizeFirstLetter = (inputString: string) => {
  return inputString.length > 1
    ? inputString.charAt(0).toUpperCase() + inputString.slice(1).toLowerCase()
    : inputString.toUpperCase();
};

const capitalizeWords = (inputString: string) => {
  return inputString
    .split(' ')
    .map((word) => capitalizeFirstLetter(word))
    .join(' ');
};

export const getNewCarLinks = limiter.wrap(async () => {
  const { data } = await directusService.get<{ main_title: string , slug: string}[]>(
    'items/cms_new_cars',
    {
      params: {
        fields: FOOTER_CARS_FIELDS,
        filter: NEW_CARS_FILTER,
        sort: 'sort',
      },
    },
  );

  return data.map((car: any) => ({
    link: `/new-cars/${car.slug}`,
    title: capitalizeWords(car.main_title),
  }));
});

export const getUsedCarLinks = limiter.wrap(async () => {
  const { data } = await directusService.get<FooterBrandReq[]>('items/brands', {
    params: {
      fields: FOOTER_BRANDS_FIELDS,
      filter: {
        vehicles: {
          type: { _in: ['1', '2'] },
          status: { _eq: 'active' },
        },
      },
      sort: 'name',
    },
  });

  return data
    .sort((brand: any) => (brand.name === 'KIA' ? -1 : 1))
    .map((brand: any) => ({
      link: `/used-cars/used/brands/${brand.name.toLowerCase()}`,
      title: toTitleCase(brand.name),
    }));
});

export const getDemoCarLinks = limiter.wrap(async () => {
  const { data } = await directusService.get<FooterBrandReq[]>('items/brands', {
    params: {
      fields: FOOTER_BRANDS_FIELDS,
      filter: {
        vehicles: {
          type: { _eq: '3' },
          status: { _eq: 'active' },
        },
      },
      sort: 'name',
    },
  });

  return data.map((brand: any) => ({
    link: `/used-cars/demo/brands/${brand.name.toLowerCase()}`,
    title: toTitleCase(brand.name),
  }));
});

export const responseBrandsForFooter = (brands: any[], link: string) => {
  enum CarTypes {
    'used-cars' = '1',
    'used' = '1',
    'konfidence' = '2',
    'demo' = '3',
    'demo-cars' = '3',
  }

  const brandsMap = brands
    .map((brand) => ({
      brandName: brand.name,
      link: `/used-cars/${link}/brands/${brand.name.toLowerCase()}`,
      id: brand.id,
      logo: brand.logo,
    }))
    .sort((a, b) => a.brandName.localeCompare(b.brandName));

  return (CarTypes as any)[link] === '3' ? getOnlyKia(brandsMap) : brandsMap;
};

export const getDekraReports = limiter.wrap(async (vehicleId) => {
  const { data } = await directusService.get('/items/dekra', {
    params: {
      fields: ['pdf_report'],
      filter: {
        vehicle: {
          _eq: vehicleId,
        },
      },
    },
  });

  return data;
});

export const getDekraReportsByVin = limiter.wrap(async (vehicleVin: string) => {
  if (!vehicleVin) return [];
  const { data } = await directusService.get('/items/dekra', {
    params: {
      fields: ['pdf_report'],
      filter: {
        vin: vehicleVin,
      },
    },
  });

  return data;
});

export const countVehicleView = limiter.wrap(
  async (vehicleId: number | string) => {
    await apiService.get(`/vehicle-popularity/count/${vehicleId}`);
  },
);

export const getVehicleDataWithPopularity = limiter.wrap(
  async <T extends PopularBodyType | PopularMake>(
    groupByField: string,
  ): Promise<T[]> => {
    const params = `limit=-1&groupBy=${groupByField}&aggregate[sum]=popularity&filter[status][_eq]=active`;
    const { data } = await directusService.get<T[]>(
      `/items/vehicles?${params}`,
    );

    return data;
  },
);

export const getVehicleSpecCount = limiter.wrap(
  async <T extends SpecCountType>(
    groupByField: string,
    initialFilterQuery: any,
    activeType?: string,
    dealershipId?: string | null,
  ): Promise<SpecCountType[]> => {
    if (groupByField === 'spec_features') {
      groupByField = 'spec_features_id';

      // Here we remove any _ands from the filter because _and now requires the full path ie. vehicles_id -> model = 5 instead of just model = 5
      const { _and: filterAnd, ...restOfFilter } = initialFilterQuery || {};

      const { data } = await directusService.get(
        '/items/vehicles_spec_features_2',
        {
          params: {
            limit: -1,
            fields: [groupByField],
            filter: {
              ...(filterAnd
                ? {
                    _and: filterAnd.map((filter: any) => ({
                      vehicles_id: filter,
                    })),
                  }
                : {}),
              vehicles_id: {
                ...(restOfFilter || {}),
                ...FILTER_ACTIVE_VEHICLES,
                ...FILTER_BY_TYPE(activeType),
                ...FILTER_BY_DEALERSHIP(dealershipId),
              },
            },
            groupBy: groupByField,
            aggregate: {
              count: '*',
            },
          },
        },
      );

      return data?.map((specType: any) => {
        return { specId: specType[groupByField], count: +specType?.count };
      });
    }

    const { data } = await directusService.get<T[]>('/items/vehicles', {
      params: {
        limit: -1,
        fields: [groupByField],
        filter: {
          ...initialFilterQuery,
          ...FILTER_ACTIVE_VEHICLES,
          ...FILTER_BY_TYPE(activeType),
          ...FILTER_BY_DEALERSHIP(dealershipId),
        },
        groupBy: groupByField,
        aggregate: {
          count: '*',
        },
      },
    });

    return data?.map((specType: any) => {
      return { specId: specType[groupByField], count: +specType?.count };
    });
  },
);

export const arraymove = (array: any[], fieldName: string) => {
  const fromIndex = array.findIndex(
    (brand) =>
      brand.name.toString().toLowerCase() ===
      fieldName.toString().toLowerCase(),
  );

  if (fromIndex) {
    array.splice(0, 0, array.splice(fromIndex, 1)?.at(0));
    return array;
  } else {
    return array;
  }
};

export const sortArrayByPopularity = (array: { popularity: number }[]) => {
  let tempArray = [...array];

  tempArray = [
    ...tempArray.sort((a, b) => {
      return b.popularity - a.popularity;
    }),
  ];
  return [...tempArray];
};

function getPriceRanges(brands: any[]) {
  const prices = brands[0]?.vehicles
    ?.map((vehicle: any) => vehicle.display_price)
    .flat()
    .filter((item: any) => item);

  const minmaxPrices = {
    min: prices ? Math.min(...prices) : 0,
    max: prices ? Math.max(...prices) : 0,
  };

  const pricesType = priceOptions.filter((price, index, arr) => {
    if (!isFinite(minmaxPrices.min) || !isFinite(minmaxPrices.max))
      return false;
    if (index === arr.length - 1)
      return (price.id as number) < minmaxPrices.max;

    return (price.id as number) > minmaxPrices.min;
  });

  return pricesType;
}

export async function getPricesFilter() {
  const usedBrands = await getHeaderBrands('used');

  return [...getPriceRanges(usedBrands)];
}

export function getAllPriceRanges() {
  return [...getPriceRanges([])];
}
